import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';
import PipelineGraph    from './components/PipelineGraph';
import AppLanes         from './components/AppLanes';
import LiveLog          from './components/LiveLog';
import Charts           from './components/Charts';
import DecisionGate     from './components/DecisionGate';
import RequirementsGate from './components/RequirementsGate';
import IaCPanel         from './components/IaCPanel';
import StateInspector   from './components/StateInspector';
import AppPortfolio     from './components/AppPortfolio';
import AuditReport      from './components/AuditReport';
import WikiPanel        from './components/WikiPanel';
import CoordinatorPanel from './components/CoordinatorPanel';
import SandboxMonitor   from './components/SandboxMonitor';
import ArchitectureView from './components/ArchitectureView';
import Dashboard        from './components/Dashboard';
import AgentCatalogInline from './components/AgentCatalogInline';
import ContextSwitcher  from './components/ContextSwitcher';
import { useOrgProject } from './contexts/OrgProjectContext';
import { apiFetch, buildAuthenticatedSSEUrl } from './apiClient';

const SSE_BASE  = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_BASE  = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const DEFAULT_THREAD_ID = process.env.REACT_APP_THREAD_ID || 'real-sdlc-001';

// ── Download Panel (shows after pipeline completes) ──────────────────────────
function DownloadPanel({ threadId }) {
  const [apps, setApps] = React.useState([]);
  React.useEffect(() => {
    const url = threadId ? `/api/generated-apps?thread_id=${encodeURIComponent(threadId)}` : `/api/generated-apps`;
    apiFetch(url)
      .then(r => r.ok ? r.json() : { apps: [] })
      .then(d => setApps(d.apps || []))
      .catch(() => {});
  }, [threadId]);

  const handleDownload = async (appName) => {
    const res = await apiFetch(`/api/generated-apps/${appName}/download`);
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${appName}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (apps.length === 0) return null;
  return (
    <div style={{ margin: '16px 0', padding: 16, background: '#DAFBE1', borderRadius: 10, border: '1px solid #1A7F37' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#1A7F37', marginBottom: 10 }}>
        📦 Generated Applications — Ready for Download
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {apps.map(app => (
          <button key={app.name} onClick={() => handleDownload(app.name)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: '#fff', border: '1px solid #1A7F37', color: '#1A7F37',
              textDecoration: 'none', cursor: 'pointer',
            }}>
            ⬇️ {app.name} ({app.files} files)
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Generate a tenant-scoped thread_id so each org/project gets its own pipeline run.
 * Format: "pipeline-<orgId>-<projectId>" or falls back to the default.
 */
function getThreadId(orgId, projectId) {
  if (orgId && projectId) {
    // Use first 8 chars of each UUID for a readable but unique thread_id
    return `pipeline-${orgId.slice(0, 8)}-${projectId.slice(0, 8)}`;
  }
  if (orgId) {
    return `pipeline-${orgId.slice(0, 8)}`;
  }
  return DEFAULT_THREAD_ID;
}

const NODE_ORDER = ['discovery','rationalization','supervisor','hitl_decision','requirements','coding','test_writing','testing','security','quality_gate','iac','audit'];
const NODE_TO_LANE = { discovery:'discover', rationalization:'rationalize', requirements:'requirements', coding:'coding', testing:'testing', security:'security' };

// ── Language color map ────────────────────────────────────────────────────────
const LANG_COLOR = {
  Python: '#0969DA', Java: '#9A6700', 'C#': '#6639BA',
  JavaScript: '#1A7F37', TypeScript: '#1A7F37', Go: '#00ACD7',
  Ruby: '#CC342D', XML: '#57606A', Unknown: '#57606A',
};

// ── App Selector Panel (collapsible) ─────────────────────────────────────────
function AppSelectorPanel({ availableApps, selectedApps, setSelectedApps, onStart, isRunning }) {
  const [expanded, setExpanded] = React.useState(true);
  const total = availableApps.filter(a => a.status !== 'removed').length;
  const DEC_COLOR = { Refactor: '#9A6700', New: '#1A7F37', Retire: '#CF222E', Replace: '#6639BA' };

  return (
    <div style={{
      margin: '8px 20px',
      background: '#fff',
      border: '1px solid #D0D7DE',
      borderRadius: 10,
      boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
      overflow: 'hidden',
    }}>
      {/* ── Collapsed header — always visible ── */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 18px', cursor: 'pointer',
          background: expanded ? '#F6F8FA' : '#fff',
          borderBottom: expanded ? '1px solid #EFF2F5' : 'none',
          userSelect: 'none',
          transition: 'background 0.15s',
        }}
      >
        {/* Chevron */}
        <span style={{
          fontSize: 12, color: '#57606A',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s', display: 'inline-block', lineHeight: 1,
        }}>▶</span>

        <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1F2E' }}>🗂 Application Selection</span>

        {/* Selection pill */}
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 99,
          background: selectedApps.size > 0 ? '#0969DA18' : '#EFF2F5',
          color: selectedApps.size > 0 ? '#0969DA' : '#57606A',
          border: `1px solid ${selectedApps.size > 0 ? '#0969DA40' : '#D0D7DE'}`,
        }}>
          {selectedApps.size} / {total} selected
        </span>

        {/* App name pills (collapsed preview) */}
        {!expanded && selectedApps.size > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', overflow: 'hidden', flex: 1 }}>
            {[...selectedApps].slice(0, 5).map(name => (
              <span key={name} style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 99,
                background: '#EFF2F5', color: '#1A1F2E', whiteSpace: 'nowrap',
                fontWeight: 600,
              }}>{name.replace('legacy-', '')}</span>
            ))}
            {selectedApps.size > 5 && (
              <span style={{ fontSize: 10, color: '#57606A', alignSelf: 'center' }}>
                +{selectedApps.size - 5} more
              </span>
            )}
          </div>
        )}

        {/* Run button — always in header */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}
          onClick={e => e.stopPropagation()}>
          <button
            onClick={onStart}
            disabled={selectedApps.size === 0 || isRunning}
            style={{
              padding: '7px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              border: 'none', cursor: (selectedApps.size === 0 || isRunning) ? 'not-allowed' : 'pointer',
              background: (selectedApps.size === 0 || isRunning) ? '#EFF2F5' : '#0969DA',
              color: (selectedApps.size === 0 || isRunning) ? '#8B949E' : '#fff',
              transition: 'all 0.15s',
              boxShadow: (selectedApps.size > 0 && !isRunning) ? '0 2px 6px rgba(9,105,218,0.35)' : 'none',
            }}
          >
            {isRunning ? '⟳ Running…' : `▶ Analyse & Modernize (${selectedApps.size})`}
          </button>
        </div>
      </div>

      {/* ── Expanded body ── */}
      {expanded && (
        <div style={{ padding: '14px 18px' }}>
          {/* Quick-select buttons */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#57606A', marginRight: 4 }}>Quick select:</span>
            <button onClick={() => setSelectedApps(new Set(availableApps.filter(a => a.status !== 'removed').map(a => a.name)))}
              style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6, fontWeight: 600,
                border: '1px solid #D0D7DE', background: '#fff', cursor: 'pointer', color: '#1A1F2E' }}>
              All ({total})
            </button>
            <button onClick={() => setSelectedApps(new Set(availableApps.filter(a => a.status === 'never').map(a => a.name)))}
              style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6, fontWeight: 700,
                border: '1px solid #1A7F37', background: '#DAFBE1', cursor: 'pointer', color: '#1A7F37' }}>
              ✨ New Only
            </button>
            <button onClick={() => setSelectedApps(new Set())}
              style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6,
                border: '1px solid #D0D7DE', background: '#fff', cursor: 'pointer', color: '#57606A' }}>
              Clear
            </button>
            {/* Legend */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: '#8B949E' }}>✨ Never run</span>
              <span style={{ fontSize: 10, color: '#1A7F37' }}>● &lt;7d</span>
              <span style={{ fontSize: 10, color: '#9A6700' }}>● 7–30d</span>
              <span style={{ fontSize: 10, color: '#CF222E' }}>● &gt;30d</span>
            </div>
          </div>

          {/* App cards grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8 }}>
            {availableApps.map(app => {
              const isSel     = selectedApps.has(app.name);
              const lc        = LANG_COLOR[app.language] || '#57606A';
              const isNew     = app.status === 'never';
              const isRemoved = app.status === 'removed';
              const daysSince = app.last_run
                ? Math.floor((Date.now() - new Date(app.last_run).getTime()) / 86400000)
                : null;
              const freshness = daysSince === null ? null
                : daysSince <= 1  ? { color: '#1A7F37', label: 'Today' }
                : daysSince <= 7  ? { color: '#1A7F37', label: `${daysSince}d ago` }
                : daysSince <= 30 ? { color: '#9A6700', label: `${daysSince}d ago` }
                : { color: '#CF222E', label: `${daysSince}d ago` };
              const decColor = DEC_COLOR[app.last_decision] || '#57606A';

              return (
                <div key={app.name}
                  onClick={() => {
                    if (isRemoved) return;
                    setSelectedApps(prev => {
                      const next = new Set(prev);
                      next.has(app.name) ? next.delete(app.name) : next.add(app.name);
                      return next;
                    });
                  }}
                  style={{
                    padding: '10px 12px', borderRadius: 8,
                    cursor: isRemoved ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                    border: `2px solid ${isSel ? lc : '#D0D7DE'}`,
                    background: isRemoved ? '#F6F8FA' : isSel ? lc + '0D' : '#FAFAFA',
                    opacity: isRemoved ? 0.5 : 1,
                    transition: 'all 0.15s',
                    position: 'relative',
                  }}
                >
                  {/* Checkmark */}
                  {isSel && (
                    <div style={{
                      position: 'absolute', top: 6, right: 8,
                      width: 16, height: 16, borderRadius: '50%',
                      background: lc, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 700,
                    }}>✓</div>
                  )}
                  {/* Language + name */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                      background: lc + '20', color: lc, flexShrink: 0 }}>{app.language}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1A1F2E',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {app.name.replace('legacy-', '')}
                    </span>
                  </div>
                  {/* Status */}
                  {isNew ? (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                      background: '#DAFBE1', color: '#1A7F37' }}>✨ Never run</span>
                  ) : isRemoved ? (
                    <span style={{ fontSize: 10, color: '#8B949E' }}>folder removed</span>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {app.last_decision && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                          background: decColor + '18', color: decColor }}>{app.last_decision}</span>
                      )}
                      {freshness && (
                        <span style={{ fontSize: 10, color: freshness.color, fontWeight: 600 }}>
                          🕐 {freshness.label}
                        </span>
                      )}
                      {app.last_coverage > 0 && (
                        <span style={{ fontSize: 10, color: '#57606A' }}>
                          {app.last_coverage.toFixed(0)}% cov
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const navigate = useNavigate();
  const { orgId, projectId } = useOrgProject();
  // Derive a tenant-scoped thread_id so each org/project pipeline is independent
  const threadId = getThreadId(orgId, projectId);
  const [runStatus, setRunStatus]         = useState('idle');
  const [nodeStatuses, setNodeStatuses]   = useState({});
  const [logEntries, setLogEntries]       = useState([]);
  const [sdlcState, setSdlcState]         = useState(null);
  const [appLanes, setAppLanes]           = useState([]);
  const [iacData, setIacData]             = useState(null);
  const [decisionData, setDecisionData]   = useState(null);
  const [requirementsData, setRequirementsData] = useState(null);
  const [chartData, setChartData]         = useState({ coverage: [], security: [], scores: [] });
  const [selectedStage, setSelectedStage] = useState(null);  const [auditReport, setAuditReport]     = useState(null);
  const [activeTab, setActiveTab]         = useState('dashboard');
  const [pipelineSubTab, setPipelineSubTab] = useState('pipeline');
  const [wikiUpdateCount, setWikiUpdateCount] = useState(0);
  const [kiroDebugMinimized, setKiroDebugMinimized] = useState(false);
  // App selection
  const [availableApps, setAvailableApps]   = useState([]);
  const [selectedApps, setSelectedApps]     = useState(new Set());
  const [showAppSelector, setShowAppSelector] = useState(false);
  const esRef    = useRef(null);
  // Raw SSE events for LiveLog (includes progress sub-steps)
  const [sseEvents, setSseEvents]         = useState([]);
  // Track which HITL gates have already been submitted this run
  const hitlSubmitted = useRef({ decisions: false, requirements: false });
  // Keep a stable ref to dispatch so the SSE handler always uses latest state setters
  const dispatchRef = useRef(null);
  // Timing: record start timestamps for nodes and per-app stages
  const nodeStartTimes = useRef({});   // { nodeName: Date.now() }
  const laneStartTimes = useRef({});   // { "appName:stageKey": Date.now() }
  const [nodeTimes, setNodeTimes]     = useState({});  // { nodeName: "1.2s" }
  const [laneTimes, setLaneTimes]     = useState({});  // { "appName:stageKey": "3.4s" }

  const fmtElapsed = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms/1000).toFixed(1)}s`;
    return `${Math.floor(ms/60000)}m ${Math.round((ms%60000)/1000)}s`;
  };

  const addLog = (level, icon, text, app = null) => {
    const ts = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLogEntries(prev => [...prev, { ts, level, icon, text, app, id: Date.now() + Math.random() }]);
  };

  const updateLane = (appName, stageKey, status, extra = {}) => {
    setAppLanes(prev => prev.map(lane =>
      lane.name === appName
        ? { ...lane, stages: { ...lane.stages, [stageKey]: status }, ...extra }
        : lane
    ));
  };

  // ── SSE event dispatcher ───────────────────────────────────────────────────
  const dispatch = (evt) => {
    const { type, data } = evt;

    // Accumulate all raw SSE events for LiveLog (skip ping + state_update)
    if (type !== 'ping' && type !== 'state_update') {
      const ts = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setSseEvents(prev => [...prev, { ...evt, ts, id: Date.now() + Math.random() }]);
    }

    switch (type) {

      // ── Pipeline node lifecycle ──────────────────────────────────────────
      case 'node_start': {
        const node = data.node || '';
        const now  = Date.now();
        if (node.startsWith('rationalize_') && data.app) {
          laneStartTimes.current[`${data.app}:rationalize`] = now;
          updateLane(data.app, 'rationalize', 'active');
        } else {
          nodeStartTimes.current[node] = now;
          setNodeStatuses(prev => ({ ...prev, [node]: 'active' }));
        }
        // Also track per-app lane stages for coding/testing/security
        if (data.app && ['coding','testing','security'].includes(node.replace(/^(test_|security_)/, ''))) {
          const laneKey = node.startsWith('test_') ? 'testing'
                        : node.startsWith('security_') ? 'security'
                        : node;
          laneStartTimes.current[`${data.app}:${laneKey}`] = now;
        }
        addLog('stage', '▶', `Starting: ${node.replace(/_/g, ' ')}`, data.app || null);
        break;
      }

      case 'node_done': {
        const node    = data.node || '';
        const now     = Date.now();
        if (node.startsWith('rationalize_') && data.app) {
          const key     = `${data.app}:rationalize`;
          const elapsed = nodeStartTimes.current[key] ? fmtElapsed(now - nodeStartTimes.current[key]) : null;
          setLaneTimes(prev => ({ ...prev, [key]: elapsed }));
          updateLane(data.app, 'rationalize', 'done', {
            recommendation: data.recommendation,
            score:          data.score,
            scores:         data.scores         || {},
            reasoning:      data.reasoning      || '',
            uncertain:      data.uncertain      || false,
            target_stack:   data.target_stack   || '',
          });
          addLog('success', '✅',
            `${data.app} → ${data.recommendation} (${data.score?.toFixed(1)}/10)${elapsed ? ` · ${elapsed}` : ''}`,
            data.app);
          setChartData(prev => ({
            ...prev,
            scores: [...prev.scores.filter(s => s.name !== data.app),
                     { name: data.app, score: data.score, rec: data.recommendation }],
          }));
        } else {
          const elapsed = nodeStartTimes.current[node]
            ? fmtElapsed(now - nodeStartTimes.current[node]) : null;
          setNodeTimes(prev => ({ ...prev, [node]: elapsed }));
          setNodeStatuses(prev => ({ ...prev, [node]: 'done' }));
        }
        break;
      }

      // ── Coordinator events (Phase 2 — Layer 1 Stage Coordination) ──────────
      case 'coordinator_start':
        addLog('stage', '🎯', `${data.coordinator}: starting sub-agents...`);
        break;

      case 'coordinator_done':
        addLog('success', '✅', `${data.coordinator}: complete (${data.duration_ms}ms)`);
        break;

      case 'sub_agent_done':
        if (data.success) {
          addLog('info', '  ↳', `${data.sub_agent} (${data.duration_ms}ms)`);
        } else {
          addLog('warn', '  ⚠', `${data.sub_agent} failed: ${data.error}`);
        }
        break;

      case 'wiki_updated':
        addLog('info', '📚', `Wiki: ${data.message} (${data.pages_created} pages)`);
        setWikiUpdateCount(c => c + 1);
        break;

      // ── Sandbox Runtime events (Phase 3 — Kiro Sandbox Runtime) ────────────
      case 'sandbox_initialize':
        // Only log for non-trivial agents to avoid noise
        if (data.agent_name && data.agent_name !== 'kiro') {
          addLog('info', '🔲', `Sandbox[${data.execution_id}] init: ${data.agent_name}${data.app_id ? ' / ' + data.app_id : ''}`);
        }
        break;

      case 'sandbox_execute':
        if (data.context_injected) {
          addLog('info', '📖', `Sandbox[${data.execution_id}] Wiki context injected`);
        }
        break;

      case 'sandbox_retry':
        addLog('warn', '🔄', `Sandbox[${data.execution_id}] retry ${data.attempt} (wait ${data.wait_s}s)`);
        break;

      case 'sandbox_publish':
        if (!data.success) {
          addLog('error', '❌', `Sandbox[${data.execution_id}] failed: ${data.agent_name}`);
        }
        break;

      case 'sandbox_complete':
        if (data.status === 'complete' && data.duration_ms > 5000) {
          addLog('info', '⚡', `Sandbox[${data.execution_id}] ${data.agent_name} ${data.duration_ms}ms${data.retries > 0 ? ` (${data.retries} retries)` : ''}`);
        }
        break;

      case 'state_update':
        setSdlcState(data);
        // Recreate lanes from state if they were missed (e.g. SSE reconnect)
        if (data.scanned_apps?.length > 0) {
          setAppLanes(prev => {
            const existing = new Set(prev.map(l => l.name));
            const missing = data.scanned_apps.filter(a => !existing.has(a.name));
            if (missing.length === 0) return prev;
            return [...prev, ...missing.map(a => ({
              name: a.name, lang: a.language, fw: a.framework,
              loc: a.loc, issues: a.issues?.length || 0,
              decision: null, recommendation: null, score: null,
              stages: { discover: 'done', rationalize: 'idle', requirements: 'idle', coding: 'idle', testing: 'idle', security: 'idle' },
            }))];
          });
        }
        // Sync lane stages from state snapshot — only fix stages stuck in 'active' after reconnect
        // Never downgrade an 'active' stage (that would hide in-progress work)
        if (data.current_phase) {
          const generatedApps = Object.keys(data.generated_code || {});
          const testResults   = data.test_results   || [];
          const secResults    = data.security_results || [];

          setAppLanes(prev => prev.map(lane => {
            const stages = { ...lane.stages };
            const testR  = testResults.find(r => r.app_name === lane.name);
            const secR   = secResults.find(r => r.app_name === lane.name);

            // Only fix stages that are stuck 'active' but state says they're done
            // Never touch stages that are already 'done', 'idle', or 'skip'
            if (stages.coding === 'active' && generatedApps.includes(lane.name)) {
              stages.coding = 'done';
            }
            if (stages.testing === 'active' && testR) {
              stages.testing = testR.failed > 0 ? 'warn' : 'done';
            }
            if (stages.security === 'active' && secR) {
              stages.security = secR.high > 0 ? 'error' : 'done';
            }
            return { ...lane, stages };
          }));
        }
        break;

      // ── Discovery ────────────────────────────────────────────────────────
      case 'app_scanned':
        setAppLanes(prev => {
          if (prev.find(l => l.name === data.name)) return prev;
          return [...prev, {
            name:   data.name,
            lang:   data.language,
            fw:     data.framework,
            loc:    data.loc,
            issues: data.issues,
            decision: null, recommendation: null, score: null,
            stages: { discover: 'done', rationalize: 'idle', requirements: 'idle', coding: 'idle', testing: 'idle', security: 'idle' },
          }];
        });
        addLog('info', '🔍', `Scanned ${data.name} — ${data.language}/${data.framework}, ${data.loc} LOC, ${data.issues} issues`);
        break;

      // ── Requirements ─────────────────────────────────────────────────────
      case 'requirements_generating':
        updateLane(data.app, 'requirements', 'active');
        addLog('info', '📝', `Generating requirements for ${data.app} (${data.decision})...`, data.app);
        break;

      case 'requirements_ready':
        updateLane(data.app, 'requirements', 'done');
        addLog('success', '✅', `Requirements ready for ${data.app} — ${data.chars} chars`, data.app);
        break;

      case 'coding_start':
        laneStartTimes.current[`${data.app}:coding`] = Date.now();
        // On retry: reset testing + security back to idle so the flow makes visual sense
        setAppLanes(prev => prev.map(lane =>
          lane.name === data.app
            ? { ...lane, stages: { ...lane.stages,
                coding:   'active',
                testing:  data.retry ? 'idle' : lane.stages.testing,
                security: data.retry ? 'idle' : lane.stages.security,
              }}
            : lane
        ));
        addLog('stage', '💻',
          `${data.retry ? `[RETRY ${data.retry_count}] ` : ''}Coding ${data.app} (${data.decision}) — calling Kiro…`,
          data.app);
        break;

      case 'coding_done': {
        const elapsed = laneStartTimes.current[`${data.app}:coding`]
          ? fmtElapsed(Date.now() - laneStartTimes.current[`${data.app}:coding`]) : null;
        setLaneTimes(prev => ({ ...prev, [`${data.app}:coding`]: elapsed }));
        updateLane(data.app, 'coding', 'done');
        addLog('success', '✅', `Code generated for ${data.app} — ${data.loc} lines${elapsed ? ` · ${elapsed}` : ''}`, data.app);
        break;
      }

      case 'testing_start':
        laneStartTimes.current[`${data.app}:testing`] = Date.now();
        updateLane(data.app, 'testing', 'active');
        addLog('info', '🧪', `Running pytest for ${data.app}...`, data.app);
        break;

      case 'testing_done': {
        const elapsed = laneStartTimes.current[`${data.app}:testing`]
          ? fmtElapsed(Date.now() - laneStartTimes.current[`${data.app}:testing`]) : null;
        setLaneTimes(prev => ({ ...prev, [`${data.app}:testing`]: elapsed }));
        const testStatus = data.failed > 0 ? 'warn' : 'done';
        updateLane(data.app, 'testing', testStatus, {
          testPassed: data.passed, testFailed: data.failed, testCoverage: data.coverage,
        });
        addLog(data.failed > 0 ? 'warn' : 'success', '🧪',
          `${data.app}: ${data.passed} passed, ${data.failed} failed, ${data.coverage}% coverage${elapsed ? ` · ${elapsed}` : ''}`, data.app);
        setChartData(prev => ({
          ...prev,
          coverage: [...prev.coverage.filter(c => c.name !== data.app),
                     { name: data.app, coverage: data.coverage }],
        }));
        break;
      }

      case 'security_start':
        laneStartTimes.current[`${data.app}:security`] = Date.now();
        updateLane(data.app, 'security', 'active');
        addLog('info', '🔒', `Security scanning ${data.app}...`, data.app);
        break;

      case 'security_done': {
        const elapsed = laneStartTimes.current[`${data.app}:security`]
          ? fmtElapsed(Date.now() - laneStartTimes.current[`${data.app}:security`]) : null;
        setLaneTimes(prev => ({ ...prev, [`${data.app}:security`]: elapsed }));
        const secStatus = data.high > 0 ? 'error' : 'done';
        updateLane(data.app, 'security', secStatus, {
          secHigh: data.high, secMedium: data.medium, secLow: data.low,
          secFindings: data.findings || [],
        });
        addLog(data.high > 0 ? 'error' : 'success', '🔒',
          `${data.app}: ${data.high} HIGH, ${data.medium} MEDIUM, ${data.low} LOW${elapsed ? ` · ${elapsed}` : ''}`, data.app);
        setChartData(prev => ({
          ...prev,
          security: [...prev.security.filter(s => s.name !== data.app),
                     { name: data.app, high: data.high, medium: data.medium, low: data.low,
                       findings: data.findings || [] }],
        }));
        break;
      }

      // ── HITL gates — do NOT change runStatus, keep SSE open ──────────────
      case 'hitl_decisions':
        // Show modal only if not already resolved (check hitlSubmitted ref)
        if (!hitlSubmitted.current.decisions) {
          setDecisionData(data);
          addLog('hitl', '⏸', 'Paused — waiting for your application decisions');
        }
        break;

      case 'hitl_requirements':
        // Show modal only if not already resolved
        if (!hitlSubmitted.current.requirements) {
          setRequirementsData(data);
          addLog('hitl', '⏸', 'Paused — requirements ready for your review');
        }
        // Store requirements text on each app's lane for the read-only view
        if (data.requirements?.length > 0) {
          data.requirements.forEach(req => {
            if (req.app_name && req.llm_draft) {
              setAppLanes(prev => prev.map(l =>
                l.name === req.app_name
                  ? { ...l, requirementsText: req.llm_draft }
                  : l
              ));
            }
          });
        }
        break;

      // ── HITL resolved by another tab ─────────────────────────────────────
      case 'hitl_resolved':
        if (data.gate === 'decisions') {
          hitlSubmitted.current.decisions = true;  // prevent re-show on any future replay
          setDecisionData(null);
          if (data.decisions) {
            Object.entries(data.decisions).forEach(([name, dec]) => {
              setAppLanes(prev => prev.map(l => l.name === name ? { ...l, decision: dec } : l));
            });
          }
          addLog('stage', '▶', 'Decisions submitted — continuing…');
        } else if (data.gate === 'requirements') {
          hitlSubmitted.current.requirements = true;  // prevent re-show on any future replay
          setRequirementsData(null);
          addLog('stage', '▶', 'Requirements approved — building code…');
        }
        break;
      case 'app_retired':
        setAppLanes(prev => prev.map(l =>
          l.name === data.name
            ? { ...l, decision: 'Retire',
                stages: Object.fromEntries(Object.keys(l.stages).map(k => [k, 'skip'])) }
            : l
        ));
        addLog('warn', '🗑️', `${data.name} → Retired — skipping all stages`);
        break;

      // ── Supervisor autonomous decisions ───────────────────────────────────
      case 'supervisor_auto_decided':
        setAppLanes(prev => prev.map(l =>
          l.name === data.app
            ? { ...l, decision: data.decision,
                stages: data.decision === 'Retire'
                  ? Object.fromEntries(Object.keys(l.stages).map(k => [k, 'skip']))
                  : l.stages }
            : l
        ));
        addLog(data.decision === 'Retire' ? 'warn' : 'success',
          data.decision === 'Retire' ? '🤖🗑️' : '🤖✅',
          `AI Decision — ${data.decision.toUpperCase()}: ${data.app} (${(data.confidence*100).toFixed(0)}% confidence) — ${data.reasoning}`,
          data.app);
        break;

      case 'supervisor_summary':
        addLog('stage', '🤖',
          `AI routing: ${data.auto_decided?.length || 0} decided automatically, ${data.hitl_needed?.length || 0} sent for your review. Confidence: ${(data.pipeline_confidence*100).toFixed(0)}%`);
        break;

      // ── Quality gate decisions ────────────────────────────────────────────
      case 'quality_decision': {
        const qIcon  = data.gate_decision === 'pass' ? '✅' : data.gate_decision === 'retry' ? '↺' : data.gate_decision === 'warn_and_proceed' ? '⚠️' : '❌';
        const qLevel = data.gate_decision === 'pass' ? 'success' : data.gate_decision === 'retry' ? 'stage' : data.gate_decision === 'warn_and_proceed' ? 'warn' : 'error';
        addLog(qLevel, `🔬${qIcon}`,
          `Quality gate [${data.app}]: ${data.gate_decision} — ${data.reasoning}${data.retry_count > 0 ? ` (retry ${data.retry_count})` : ''}`,
          data.app);
        if (data.gate_decision === 'retry') {
          // Reset the retry stages to idle — coding_start will set them active
          setAppLanes(prev => prev.map(l =>
            l.name === data.app
              ? { ...l, stages: { ...l.stages, coding: 'idle', testing: 'idle', security: 'idle' }}
              : l
          ));
        }
        break;
      }

      // ── IaC sub-agent ────────────────────────────────────────────────────
      case 'iac_start':
        setNodeStatuses(prev => ({ ...prev, iac_terraform: 'active', iac_cdk: 'idle' }));
        addLog('iac', '🏗️', 'Generating cloud infrastructure scripts — Terraform + CDK in parallel');
        break;

      case 'iac_node_done':
        setNodeStatuses(prev => ({ ...prev, [`iac_${data.node}`]: 'done' }));
        if (data.node === 'terraform') {
          setNodeStatuses(prev => ({ ...prev, iac_cdk: 'active' }));
          addLog('iac', '🏗️', `Terraform infrastructure scripts generated — ${data.lines} lines`);
        } else {
          addLog('iac', '🏗️', `AWS CDK scripts generated — ${data.lines} lines`);
        }
        break;

      case 'iac_complete':
        setIacData(data);
        setNodeStatuses(prev => ({ ...prev, iac_cdk: 'done' }));
        addLog('iac', '✅', 'Cloud infrastructure scripts ready');
        break;

      // ── Re-run ───────────────────────────────────────────────────────────
      case 'rerun_start': {
        const idx = NODE_ORDER.indexOf(data.stage);
        setNodeStatuses(prev => {
          const next = { ...prev };
          if (idx >= 0) NODE_ORDER.slice(idx).forEach(n => { next[n] = 'idle'; });
          return next;
        });
        if (idx >= 0) {
          const laneKeysToReset = NODE_ORDER.slice(idx).map(n => NODE_TO_LANE[n]).filter(Boolean);
          setAppLanes(prev => prev.map(lane => ({
            ...lane,
            stages: Object.fromEntries(
              Object.entries(lane.stages).map(([k, v]) =>
                laneKeysToReset.includes(k) ? [k, 'idle'] : [k, v]
              )
            ),
          })));
        }
        addLog('stage', '↺', `Retrying from: ${data.stage}`);
        break;
      }

      case 'pipeline_pausing':
        addLog('warn', '⏸', `Pause requested — will stop at next checkpoint${data.stage ? ` (${data.stage})` : ''}`);
        break;

      case 'pipeline_paused':
        addLog('warn', '⏸', `Paused at: ${data.stage || 'checkpoint'}`);
        break;

      case 'pipeline_resumed':
        addLog('stage', '▶', 'Resumed — continuing modernization');
        break;

      // ── Terminal events ───────────────────────────────────────────────────
      case 'audit_report':
        setAuditReport(data);
        break;

      case 'complete':
        setRunStatus('complete');
        setDecisionData(null);
        setRequirementsData(null);
        // Force-sync all lane stages — clear any stuck 'active' stages
        setAppLanes(prev => prev.map(lane => ({
          ...lane,
          stages: Object.fromEntries(
            Object.entries(lane.stages).map(([k, v]) =>
              v === 'active' ? [k, 'done'] : [k, v]
            )
          ),
        })));
        // Force all pipeline nodes out of 'active'
        setNodeStatuses(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(k => { if (next[k] === 'active') next[k] = 'done'; });
          return next;
        });
        addLog('success', '🎉', `Modernization complete! ${data.deployment_status}`);
        break;

      case 'error':
        setRunStatus('error');
        addLog('error', '❌', `Error: ${data.message}`);
        break;

      default:
        break;
    }
  };

  // ── Restore full state on mount (refresh / new tab) ─────────────────────
  useEffect(() => {
    const restoreState = async () => {
      try {
        const r = await apiFetch(`/api/status?thread_id=${threadId}`);
        if (!r.ok) return;
        const s = await r.json();

        if (s.status === 'idle' || (!s.is_active && !s.is_complete)) return;

        // ── Restore node statuses ──────────────────────────────────────────
        if (s.node_statuses && Object.keys(s.node_statuses).length > 0) {
          setNodeStatuses(s.node_statuses);
        }

        // ── Restore app lanes ──────────────────────────────────────────────
        if (s.app_lanes?.length > 0) {
          setAppLanes(s.app_lanes);
        }

        // ── Restore chart data ─────────────────────────────────────────────
        if (s.chart_data) {
          setChartData({
            scores:   s.chart_data.scores   || [],
            coverage: s.chart_data.coverage || [],
            security: s.chart_data.security || [],
          });
        }

        // ── Restore IaC panel ──────────────────────────────────────────────
        if (s.iac_data) setIacData(s.iac_data);

        // ── Pre-mark HITL gates as submitted if pipeline is past them ─────
        // This prevents buffer replay from re-showing already-dismissed modals
        if (!s.is_paused_decisions) {
          hitlSubmitted.current.decisions = true;
        }
        if (!s.is_paused_requirements) {
          hitlSubmitted.current.requirements = true;
        }

        // ── Restore HITL modals if paused ──────────────────────────────────
        if (s.is_paused_decisions && s.decision_data) {
          hitlSubmitted.current.decisions = false; // allow modal to show
          setDecisionData(s.decision_data);
        }
        if (s.is_paused_requirements && s.requirements_data) {
          hitlSubmitted.current.requirements = false; // allow modal to show
          setRequirementsData(s.requirements_data);
        }

        // ── Set run status + open SSE immediately ──────────────────────────
        if (s.is_complete) {
          setRunStatus('complete');
          addLog('info', '✅', 'Pipeline completed — state restored from server');
        } else if (s.is_active) {
          setRunStatus('running');
          addLog('stage', '🔗', `Rejoined pipeline (${s.status}) — replaying events…`);
          // Open SSE immediately — don't wait for useEffect to re-run
          if (!esRef.current) {
            const sseUrl = await buildAuthenticatedSSEUrl(`/api/stream?thread_id=${threadId}`);
            const es = new EventSource(sseUrl);
            esRef.current = es;
            es.onmessage = (e) => {
              try { dispatchRef.current(JSON.parse(e.data)); } catch (_) {}
            };
            es.onerror = () => {
              es.close();
              esRef.current = null;
              setTimeout(async () => {
                if (esRef.current === null && runStatusRef.current === 'running') {
                  const retryUrl = await buildAuthenticatedSSEUrl(`/api/stream?thread_id=${threadId}`);
                  const es2 = new EventSource(retryUrl);
                  esRef.current = es2;
                  es2.onmessage = (e) => { try { dispatchRef.current(JSON.parse(e.data)); } catch (_) {} };
                  es2.onerror = () => { es2.close(); esRef.current = null; };
                }
              }, 1500);
            };
          }
        }
      } catch (_) {}
    };
    restoreState();
  }, [threadId]); // eslint-disable-line

  // ── Fetch available apps from the project's app registry ──────────────
  useEffect(() => {
    if (!orgId || !projectId) return;
    const fetchApps = async () => {
      try {
        const r = await apiFetch(`/api/orgs/${orgId}/projects/${projectId}/apps`);
        if (!r.ok) return;
        const data = await r.json();
        const appList = Array.isArray(data) ? data : (data.data || data.results || data.items || []);
        // Map registry apps to the shape AppSelectorPanel expects
        const apps = appList.map(a => ({
          name: a.name,
          language: a.language || 'Unknown',
          files: 0,
          last_run: null,
          status: 'never',
          ...a,
        }));
        setAvailableApps(apps);
        // Default: select all apps
        setSelectedApps(new Set(apps.filter(a => a.status !== 'removed').map(a => a.name)));
      } catch (_) {}
    };
    fetchApps();
  }, [orgId, projectId]); // eslint-disable-line

  // Keep dispatchRef current so SSE handler always has latest closures
  dispatchRef.current = dispatch;

  // ── SSE connection — always open when running, uses ref to avoid stale closures ──
  const runStatusRef = useRef(runStatus);
  runStatusRef.current = runStatus;

  // ── Status polling fallback — keeps UI in sync if SSE drops ──
  useEffect(() => {
    if (runStatus !== 'running') return;
    const pollInterval = setInterval(async () => {
      try {
        const r = await apiFetch(`/api/status?thread_id=${threadId}`);
        if (!r.ok) return;
        const s = await r.json();

        // Update node statuses from server state
        if (s.node_statuses) {
          setNodeStatuses(prev => {
            const merged = { ...prev };
            Object.entries(s.node_statuses).forEach(([k, v]) => {
              // Only update if server has newer info (done > active > idle)
              if (v === 'done' || (v === 'active' && merged[k] !== 'done')) {
                merged[k] = v;
              }
            });
            return merged;
          });
        }

        // Update app lanes from server state
        if (s.app_lanes?.length > 0) {
          setAppLanes(prev => {
            if (prev.length === 0) return s.app_lanes;
            // Merge: update stages that are further along
            return prev.map(lane => {
              const serverLane = s.app_lanes.find(l => l.name === lane.name);
              if (!serverLane) return lane;
              const stages = { ...lane.stages };
              Object.entries(serverLane.stages || {}).forEach(([k, v]) => {
                if (v === 'done' || v === 'warn' || v === 'error' || v === 'skip') {
                  stages[k] = v;
                }
              });
              return { ...lane, ...serverLane, stages };
            });
          });
        }

        // Detect completion
        if (s.status === 'complete' && runStatusRef.current === 'running') {
          setRunStatus('complete');
          dispatchRef.current({ type: 'complete', data: { deployment_status: 'complete', messages: s.messages || [] } });
        }
      } catch (_) {}
    }, 5000);  // Poll every 5s for responsiveness
    return () => clearInterval(pollInterval);
  }, [runStatus]);

  useEffect(() => {
    if (runStatus !== 'running') {
      // Close any open connection when not running
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      return;
    }
    if (esRef.current) return; // already open

    const openSSE = async () => {
      const sseUrl = await buildAuthenticatedSSEUrl(`/api/stream?thread_id=${threadId}`);
      const es = new EventSource(sseUrl);
      esRef.current = es;
      es.onmessage = (e) => {
        try { dispatchRef.current(JSON.parse(e.data)); } catch (_) {}
      };
      es.onerror = () => {
        es.close();
        esRef.current = null;
        // Use ref so we always check the CURRENT runStatus, not the stale closure value
        setTimeout(() => {
          if (esRef.current === null && runStatusRef.current === 'running') openSSE();
        }, 1500);
      };
    };
    openSSE();

    return () => {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
    };
  }, [runStatus]); // eslint-disable-line

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleStart = async () => {
    // Reset all UI state
    setNodeStatuses({}); setLogEntries([]); setSdlcState(null);
    setDecisionData(null); setRequirementsData(null);
    setIacData(null); setAppLanes([]);
    setChartData({ coverage: [], security: [], scores: [] });
    setSelectedStage(null);    setAuditReport(null);
    setSseEvents([]);
    hitlSubmitted.current = { decisions: false, requirements: false };
    nodeStartTimes.current = {};
    laneStartTimes.current = {};
    setNodeTimes({});
    setLaneTimes({});
    if (esRef.current) { esRef.current.close(); esRef.current = null; }

    addLog('stage', '🚀', 'Starting modernization...');
    const res = await apiFetch(`/api/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        thread_id: threadId,
        org_id: orgId || undefined,
        project_id: projectId || undefined,
        force: true,
        selected_apps: [...selectedApps],  // send selected app names
      }),
    });
    if (res.ok) {
      setRunStatus('running');
      setNodeStatuses({ discovery: 'active' });
      // Open SSE immediately — don't wait for useEffect re-run
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      const sseUrl = await buildAuthenticatedSSEUrl(`/api/stream?thread_id=${threadId}`);
      const es = new EventSource(sseUrl);
      esRef.current = es;
      es.onmessage = (e) => {
        try { dispatchRef.current(JSON.parse(e.data)); } catch (_) {}
      };
      es.onerror = () => {
        es.close();
        esRef.current = null;
        setTimeout(async () => {
          if (esRef.current === null && runStatusRef.current === 'running') {
            const retryUrl = await buildAuthenticatedSSEUrl(`/api/stream?thread_id=${threadId}`);
            const es2 = new EventSource(retryUrl);
            esRef.current = es2;
            es2.onmessage = (e) => { try { dispatchRef.current(JSON.parse(e.data)); } catch (_) {} };
            es2.onerror = () => { es2.close(); esRef.current = null; };
          }
        }, 1500);
      };
    } else {
      addLog('error', '❌', 'Failed to start — please try again');
    }
  };

  const handleDecisions = async (decisions, selectedModules) => {
    hitlSubmitted.current.decisions = true;
    Object.entries(decisions).forEach(([name, dec]) => {
      setAppLanes(prev => prev.map(l => l.name === name ? { ...l, decision: dec } : l));
    });
    setDecisionData(null);
    addLog('stage', '▶', 'Decisions saved — drafting requirements...');
    await apiFetch(`/api/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ thread_id: threadId, decisions, selected_modules: selectedModules || {} }),
    });
  };

  const handleApproveRequirements = async (requirements) => {
    hitlSubmitted.current.requirements = true;  // prevent modal re-show on SSE replay
    setRequirementsData(null);
    addLog('stage', '▶', 'Requirements approved — building code...');
    await apiFetch(`/api/approve-requirements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ thread_id: threadId, requirements }),
    });
  };

  // Toggle stage filter for LiveLog — click same stage to clear
  const handleStageClick = (stageId) => {
    setSelectedStage(prev => prev === stageId ? null : stageId);
  };

  const canStart    = ['idle', 'complete', 'error'].includes(runStatus);
  const isRunning   = runStatus === 'running';
  const isHitlPaused = !!(decisionData || requirementsData);
  const statusLabel = isHitlPaused ? 'awaiting your input'
                    : runStatus === 'running' ? 'in progress'
                    : runStatus === 'complete' ? 'completed'
                    : runStatus === 'error' ? 'error'
                    : runStatus.replace(/_/g, ' ');

  return (
    <div className="main-layout">
      {/* ── Top-Level Tabs: Dashboard vs Pipeline ─────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 0, background: '#0F172A', padding: '0 20px',
      }}>
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#fff', marginRight: 24, padding: '10px 0' }}>⚡ AI Modernization Hub</h1>
        <ContextSwitcher />
        <button
          onClick={() => navigate('/orgs')}
          style={{
            marginLeft: 12, padding: '6px 14px', fontSize: 12, fontWeight: 600,
            border: '1px solid #334155', borderRadius: 6, cursor: 'pointer',
            background: 'transparent', color: '#94A3B8',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.target.style.background = '#1E293B'; e.target.style.color = '#fff'; }}
          onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = '#94A3B8'; }}
        >
          ⚙ Manage Organisations
        </button>
        <div style={{ flex: 1 }} />
        {[
          { id: 'dashboard', icon: '📊', label: 'Dashboard' },
          { id: 'pipeline',  icon: '🔀', label: 'Pipeline' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '10px 20px', fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 500,
            cursor: 'pointer', border: 'none', background: 'transparent',
            color: activeTab === tab.id ? '#fff' : '#94A3B8',
            borderBottom: activeTab === tab.id ? '3px solid #3B82F6' : '3px solid transparent',
            transition: 'all 0.15s',
          }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ DASHBOARD TAB — completely independent, clean page ═══ */}
      {activeTab === 'dashboard' && (
        <Dashboard />
      )}

      {/* ═══ PIPELINE TAB — all pipeline tools ═══ */}
      {activeTab === 'pipeline' && (
        <>
          <header className="app-header">
            <span className={`status-pill ${isHitlPaused ? 'paused' : runStatus.startsWith('paused') ? 'paused' : runStatus}`}>
              {statusLabel}
            </span>
            <div className="header-spacer" />
            <button className="btn btn-primary" onClick={handleStart}
              disabled={!canStart || selectedApps.size === 0}>
              {isRunning ? '⟳ Running…' : `▶ Analyse & Modernize${selectedApps.size > 0 ? ` (${selectedApps.size})` : ''}`}
            </button>
          </header>

          {/* App Selection Panel — always visible */}
          {availableApps.length > 0 && (
            <AppSelectorPanel
              availableApps={availableApps}
              selectedApps={selectedApps}
              setSelectedApps={setSelectedApps}
              onStart={handleStart}
              isRunning={isRunning}
            />
          )}

          {decisionData     && <DecisionGate data={decisionData} onSubmit={handleDecisions} />}
          {requirementsData && <RequirementsGate data={requirementsData} onApprove={handleApproveRequirements} />}

          <AppPortfolio />

          {/* Sub-tabs for pipeline views */}
          <div style={{
            display: 'flex', gap: 4, padding: '8px 20px 0',
            borderBottom: '2px solid #D0D7DE', background: '#F6F8FA',
          }}>
            {[
              { id: 'pipeline',     icon: '🔀', label: 'Pipeline' },
              { id: 'coordinators', icon: '🎯', label: 'Coordinators' },
              { id: 'sandbox',      icon: '🔲', label: 'Sandbox' },
              { id: 'wiki',         icon: '📚', label: `Wiki${wikiUpdateCount > 0 ? ` (${wikiUpdateCount})` : ''}` },
              { id: 'agents',       icon: '🤖', label: 'Agents' },
              { id: 'architecture', icon: '🏛️', label: 'Architecture' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setPipelineSubTab(tab.id)} style={{
                padding: '7px 16px', borderRadius: '8px 8px 0 0', fontSize: 12,
                fontWeight: pipelineSubTab === tab.id ? 700 : 500,
                cursor: 'pointer', border: 'none',
                background: pipelineSubTab === tab.id ? '#fff' : 'transparent',
                color: pipelineSubTab === tab.id ? '#0969DA' : '#57606A',
                borderBottom: pipelineSubTab === tab.id ? '2px solid #0969DA' : '2px solid transparent',
                marginBottom: -2,
                transition: 'all 0.15s',
              }}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          <div className="top-section">
            {/* Pipeline Sub-Tab */}
            {pipelineSubTab === 'pipeline' && (<>
              <PipelineGraph
                nodeStatuses={nodeStatuses} lanes={appLanes}
                nodeTimes={nodeTimes}
                onStageClick={handleStageClick} selectedStage={selectedStage}
              />
              <LiveLog entries={sseEvents} selectedStage={selectedStage} onClearFilter={() => setSelectedStage(null)} />
              {appLanes.length > 0 && (
                <AppLanes lanes={appLanes} laneTimes={laneTimes} />
              )}
              {(chartData.coverage.length > 0 || chartData.security.length > 0 || chartData.scores.length > 0) && (
                <Charts data={chartData} />
              )}
              {auditReport && <AuditReport report={auditReport} />}
              {/* Download generated apps */}
              {runStatus === 'complete' && (
                <DownloadPanel threadId={threadId} />
              )}
              <div className="bottom-section">
                <div className="col-left">
                  {iacData && <IaCPanel iacData={iacData} />}
                </div>
                <div className="col-right">
                  <StateInspector sdlcState={sdlcState} />
                </div>
              </div>
            </>)}

            {/* Coordinators Sub-Tab */}
            <div style={{ display: pipelineSubTab === 'coordinators' ? 'grid' : 'none',
              gridTemplateColumns: '1fr 1fr', gap: 16, padding: '4px 0' }}>
              <CoordinatorPanel sseEvents={sseEvents} />
              <div style={{ minWidth: 0 }}>
                <LiveLog entries={sseEvents.filter(e => {
                  if (['coordinator_start','coordinator_done','sub_agent_done','wiki_updated'].includes(e.type)) return true;
                  if (['node_start','node_done'].includes(e.type) &&
                      ['discovery','rationalization','supervisor','requirements','coding'].includes(e.data?.node)) return true;
                  if (e.type === 'progress' &&
                      ['discovery','rationalization','requirements','coding','supervisor'].includes(e.data?.stage)) return true;
                  return false;
                })} selectedStage={null} onClearFilter={() => {}} />
              </div>
            </div>

            {/* Sandbox Sub-Tab */}
            <div style={{ display: pipelineSubTab === 'sandbox' ? 'flex' : 'none',
              flexDirection: 'column', gap: 16, padding: '4px 0' }}>
              <SandboxMonitor sseEvents={sseEvents} />
              <LiveLog entries={sseEvents.filter(e =>
                e.type?.startsWith('sandbox_') || e.type?.startsWith('kiro_cli') || e.type === 'sandbox_cache_hit'
              )} selectedStage={null} onClearFilter={() => {}} />
            </div>

            {/* Agents Sub-Tab */}
            {pipelineSubTab === 'agents' && (
              <AgentCatalogInline />
            )}

            {/* Wiki Sub-Tab */}
            <div style={{ display: pipelineSubTab === 'wiki' ? 'block' : 'none', height: 600 }}>
              <WikiPanel wikiUpdateCount={wikiUpdateCount} />
            </div>

            {/* Architecture Sub-Tab */}
            {pipelineSubTab === 'architecture' && (
              <ArchitectureView nodeStatuses={nodeStatuses} sseEvents={sseEvents} />
            )}
          </div>
        </>
      )}

    </div>
  );
}